import { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model.ts';
import { Animal, PreviousStudy } from '@/model/Animal.model.ts';
import { DateTimeRenderer, DistanceToNow } from '@/components/UI/DateRenderers/DateRenderers.tsx';
import { FC, useMemo } from 'react';
import Table from '@/components/UI/Table';
import { PickRequired } from '@/model/Common.model.ts';
import Link from '@/components/UI/Link';
import { RouteService } from '@/support/RouteService.ts';
import { _notNil } from '@/littledash.ts';
import UserAvatar from '@/components/UI/UserAvatar';

const generateColumns = (hasStudyCode: boolean): Array<TanStackColumnDef<PreviousStudy>> => {
  return [
    {
      header: 'Study name',
      id: 'study_name',
      accessorFn: (row: PreviousStudy) => row.study?.name,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: PreviousStudy } }) =>
        _notNil(original.study?.access) ? (
          <Link
            className="link blue"
            to={RouteService.web({ route: 'studies.show', path: { id: original.study?.id } }).route}
          >
            {original.study?.name}
          </Link>
        ) : (
          original.study?.name
        ),
    },
    ...(hasStudyCode
      ? ([
          {
            header: 'Study code',
            id: 'study_code',
            accessorFn: (row: PreviousStudy) => row.study?.code,
            enableSorting: true,
            cell: ({ row: { original } }: { row: { original: PreviousStudy } }) => original?.study?.code ?? '-',
          },
        ] as Array<TanStackColumnDef<PreviousStudy>>)
      : []),
    {
      header: 'Removed from study',
      id: 'moved_at',
      accessorFn: (row: PreviousStudy) => row.moved?.at,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: PreviousStudy } }) => (
        <>
          <DateTimeRenderer value={original.moved?.at} />
          <span className="ml2 dark-gray f8">
            (<DistanceToNow value={original.moved?.at} />)
          </span>
        </>
      ),
    },
    {
      header: 'Removed by',
      id: 'moved_by',
      accessorFn: (row: PreviousStudy) => row.moved?.by?.name,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: PreviousStudy } }) => (
        <UserAvatar user={original.moved?.by} tooltip={original.moved?.by?.name} />
      ),
    },
  ];
};

export const PreviousStudies: FC<{ animal: PickRequired<Animal, 'study' | 'previous_studies'> }> = ({ animal }) => {
  const { columns, previousStudies } = useMemo(() => {
    const previousStudies: Array<PreviousStudy> = animal?.previous_studies ?? [];
    const hasStudyCode = previousStudies.some((study) => _notNil(study.study.code));
    return { previousStudies, columns: generateColumns(hasStudyCode) };
  }, [animal]);
  if (previousStudies.length === 0) {
    return null;
  }

  return (
    <>
      <div className="lh-title f4 mt3 mb3">Previous studies</div>
      <div className="ui-card mb3">
        <div className="overflow-table">
          <Table columns={columns} data={previousStudies} />
        </div>
      </div>
    </>
  );
};
